import { supabase } from '@/lib/supabase';
import { createContext } from '@/utils/context';
import { Session } from '@supabase/supabase-js';
import { PropsWithChildren, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface AuthContextType {
  session: Session | null;
  isLoading: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithFacebook: () => Promise<void>;
  signInWithEmail: (
    email: string,
    password?: string,
  ) => Promise<{
    data: { user: any; session: any } | null;
    error: Error | null;
  }>;
  signOut: () => Promise<void>;
  verifyOtp: (
    email: string,
    token: string,
  ) => Promise<{
    data: { session: any } | null;
    error: Error | null;
  }>;
}

const [useAuth, AuthContextProvider] = createContext<AuthContextType>('Auth');

const LoadingScreen = () => {
  return (
    <div className="h-screen w-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900" />
    </div>
  );
};

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const navigate = useNavigate();
  const [currentSession, setCurrentSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setCurrentSession(session);
      setIsLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setCurrentSession(session);
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signInWithGoogle = async () => {
    await supabase.auth.signInWithOAuth({
      provider: 'google',
    });
  };

  const signInWithFacebook = async () => {
    await supabase.auth.signInWithOAuth({
      provider: 'facebook',
    });
  };

  const signInWithEmail = async (email: string, password?: string) => {
    try {
      if (password) {
        // Use password authentication for test email
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        return { data, error };
      } else {
        // Use magic link for regular users
        const { data, error } = await supabase.auth.signInWithOtp({
          email,
          options: {
            shouldCreateUser: true,
            emailRedirectTo: `${window.location.origin}/auth/callback`,
          },
        });

        return { data, error };
      }
    } catch (error) {
      return { data: null, error: error as Error };
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    navigate('/auth');
  };

  const verifyOtp = async (email: string, token: string) => {
    try {
      const { data, error } = await supabase.auth.verifyOtp({
        email,
        token,
        type: 'email',
      });

      return { data, error };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  const values = {
    session: currentSession,
    isLoading,
    signInWithGoogle,
    signInWithFacebook,
    signInWithEmail,
    signOut,
    verifyOtp,
  };

  return <AuthContextProvider value={values}>{children}</AuthContextProvider>;
};

export { useAuth };
